import React from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray } from 'redux-form';
import { renderFieldForm } from '../../../shared/formComponents';
import Tooltip from '../../../../../shared/components/widgets/Tooltip';

/**
 * Reusable dynamic audience list component.
 *
 * Renders a small section with a "+ Add" button that appends a new row for the
 * given redux-form FieldArray. Each row is a text input (an audience ID) with a
 * remove button. Values are stored as a plain array of strings, which the
 * budget_json serializer maps to the platform-specific targeting path.
 *
 * Generic on purpose so it can be reused for include/exclude lists on any
 * platform (Meta today, Google/TikTok later) by simply passing a different
 * `name` and label.
 */
const renderAudienceList = ({
  fields,
  label,
  tooltip,
  placeholder,
  addButtonLabel,
  rowComponent,
  rowComponentProps,
  meta: { error },
}) => (
  <div className="form__form-group">
    <span className="form__form-group-label">
      {label}
      {tooltip ? (
        <>
          {' '}
          <Tooltip id={`${fields.name}-tooltip`} msg={tooltip} placement="top" />
        </>
      ) : null}
    </span>

    <div style={{ width: '100%' }}>
      {fields.length === 0 && (
        <div style={{ fontSize: '0.85em', color: '#999', marginBottom: '8px' }}>
          None added yet.
        </div>
      )}

      {fields.map((member, index) => (
        <div
          key={member}
          className="form__form-group-field"
          style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}
        >
          <div style={{ flex: 1 }}>
            <Field
              name={member}
              component={rowComponent || renderFieldForm}
              type="text"
              placeholder={placeholder}
              {...rowComponentProps}
            />
          </div>
          <button
            type="button"
            onClick={() => fields.remove(index)}
            aria-label="Remove"
            style={{

              flex: '0 0 auto',
              border: '1px solid rgba(255, 0, 0, 0.4)',
              backgroundColor: 'rgba(255, 0, 0, 0.08)',
              color: '#ff4861',
              borderRadius: '4px',
              padding: '6px 10px',
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={() => fields.push('')}
        style={{
          border: '1px solid rgba(145, 190, 35, 0.5)',
          backgroundColor: 'rgba(145, 190, 35, 0.1)',
          color: '#91BE23',
          borderRadius: '4px',
          padding: '6px 12px',
          cursor: 'pointer',
        }}
      >
        + {addButtonLabel}
      </button>

      {error && <span className="form__form-group-error" style={{ display: 'block', marginTop: '6px' }}>{error}</span>}
    </div>
  </div>
);

renderAudienceList.propTypes = {
  fields: PropTypes.shape({
    name: PropTypes.string,
    length: PropTypes.number,
    map: PropTypes.func,
    push: PropTypes.func,
    remove: PropTypes.func,
  }).isRequired,
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  placeholder: PropTypes.string,
  addButtonLabel: PropTypes.string,
  rowComponent: PropTypes.elementType,
  rowComponentProps: PropTypes.shape(),
  meta: PropTypes.shape({
    error: PropTypes.string,
  }),
};

renderAudienceList.defaultProps = {
  tooltip: null,
  placeholder: 'Enter audience ID',
  addButtonLabel: 'Add',
  rowComponent: null,
  rowComponentProps: {},
  meta: {},
};

/**
 * AudienceFields
 * @param {string} name - redux-form field name (array of strings)
 * @param {string} label - Section label
 * @param {string} tooltip - Optional tooltip text
 * @param {string} placeholder - Row input placeholder
 * @param {string} addButtonLabel - Label for the add button
 * @param {Function} rowComponent - Optional custom redux-form Field component
 *   to render each row's input (defaults to a plain text input). Used e.g. to
 *   swap in a searchable typeahead field with a name-lookup display.
 * @param {Object} rowComponentProps - Extra props forwarded to rowComponent
 */
const AudienceFields = ({
  name, label, tooltip, placeholder, addButtonLabel, rowComponent, rowComponentProps,
}) => (
  <FieldArray
    name={name}
    component={renderAudienceList}
    label={label}
    tooltip={tooltip}
    placeholder={placeholder}
    addButtonLabel={addButtonLabel}
    rowComponent={rowComponent}
    rowComponentProps={rowComponentProps}
  />
);

AudienceFields.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  placeholder: PropTypes.string,
  addButtonLabel: PropTypes.string,
  rowComponent: PropTypes.elementType,
  rowComponentProps: PropTypes.shape(),
};

AudienceFields.defaultProps = {
  tooltip: null,
  placeholder: 'Enter audience ID',
  addButtonLabel: 'Add',
  rowComponent: null,
  rowComponentProps: {},
};

export default AudienceFields;
